import logoUrl from "../assets/postmath-logo.svg";
import { Alert } from "../components/Alert";
import { ButtonLink } from "../components/Button";
import { GoogleMark } from "../components/GoogleMark";
import { hostOf } from "../lib/format";
import { loginURL, type SsoParams } from "../lib/sso";

/**
 * 로그인 화면 (`/`, `/login`).
 *
 * 시각 디자인은 연구노트 관리(MVP.ResearchNote-Manager `login.html`)를 따릅니다 — 흐린 장식 원
 * 두 개 위에 떠 있는 카드, 브랜드 로고, 화살표가 붙은 Google 버튼. 색은 그쪽의 indigo
 * 대신 사내 토큰(`blue-*` / `grey-*`)으로 옮겼습니다.
 *
 * 버튼은 API 의 `/auth/login` 으로 나가는 **전체 이동**이라 `<a>` 입니다(fetch 가 아닙니다 —
 * OIDC 왕복은 브라우저 리다이렉트 체인이어야 합니다). 위임 파라미터(site/return_to/state)는
 * 해석하지 않고 그대로 실어 보냅니다 — 검증은 서버의 단일 지점입니다(`src/lib/sso.ts` 주석).
 */
export function LoginScreen({ params, error }: { params: SsoParams; error: string | null }) {
  /*
   * 복귀 호스트를 함께 보여 줍니다 — site_code 는 짧은 기계 식별자라 사용자가 어느 서비스인지
   * 알기 어렵습니다. 값은 검증 전 사용자 입력이므로 **호스트만** 텍스트로 렌더하고, 파싱에
   * 실패하면 아예 표시하지 않습니다(링크로 만들지 않는 것이 핵심 — open-redirect 유인을 없앱니다).
   */
  const host = params.isDelegated ? hostOf(params.returnTo) : "";

  return (
    <div className="flex min-h-[100dvh] items-center justify-center overflow-hidden px-[16px] py-[40px] sm:px-[24px]">
      <div className="relative w-full max-w-[400px]">
        {/* 장식용 흐린 원 — 카드 뒤에 깔려 배경에 깊이만 줍니다. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-[80px] -top-[80px] h-[288px] w-[288px] rounded-full bg-blue-100 opacity-40 mix-blend-multiply blur-[64px]"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-[80px] -right-[80px] h-[288px] w-[288px] rounded-full bg-blue-200 opacity-30 mix-blend-multiply blur-[64px]"
        />

        <div className="relative rounded-[16px] bg-white px-[40px] py-[40px] text-center shadow-[0_20px_25px_-5px_rgba(25,25,28,0.06),0_8px_10px_-6px_rgba(25,25,28,0.06)] ring-1 ring-grey-100/70 max-sm:px-[28px]">
          <div className="mb-[32px] flex justify-center">
            {/* 바로 아래 제목이 서비스명을 읽어 주므로 로고는 장식으로 둡니다(alt=""). */}
            <img src={logoUrl} alt="" width={62} height={64} className="h-[64px] w-auto" />
          </div>
          <h1 className="m-0 mb-[40px] text-[24px] font-bold tracking-[-0.02em] text-grey-900">
            포스트매스 통합 로그인
          </h1>

          {params.isDelegated ? (
            <div className="mb-[24px] flex items-center gap-[10px] rounded-xl border border-grey-100 bg-blue-025 px-[16px] py-[14px] text-left">
              <span aria-hidden="true" className="shrink-0 text-[20px]">
                🔗
              </span>
              <span className="min-w-0">
                <span className="block text-[12px] font-semibold text-grey-500">로그인 후 이동</span>
                <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-[14px] font-bold">
                  {host || params.site}
                </span>
                {host ? (
                  <span className="block text-[12px] font-semibold text-grey-500">{params.site}</span>
                ) : null}
              </span>
            </div>
          ) : null}

          {error ? <Alert kind="error">{error}</Alert> : null}

          <ButtonLink variant="google" size="lg" href={loginURL(params)} rel="nofollow">
            <GoogleMark />
            <span>Google 계정으로 로그인</span>
            <svg
              aria-hidden="true"
              className="h-[16px] w-[16px] text-grey-400 transition-transform group-hover:translate-x-[2px]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </ButtonLink>

          <p className="mx-auto mt-[32px] text-[12px] leading-[1.5] text-grey-500">
            사내 구성원 전용입니다.
            <br />
            계정 권한 문의는 플랫폼 관리자에게 연락해 주세요.
          </p>
        </div>
      </div>
    </div>
  );
}
